import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS, type PermissionCode } from '@aps/contracts';
import {
  IconBolt,
  IconBuilding,
  IconCard,
  IconCar,
  IconChart,
  IconFolders,
  IconGear,
  IconMap,
  IconOrders,
  IconPin,
  IconPlus,
  IconScroll,
  IconSearch,
  IconShuffle,
  IconTracking,
  IconTruck,
  IconUpload,
  IconUsers,
  IconWrench,
  type IconProps,
} from '@aps/ui';
import { api } from '../lib/api.js';
import { can } from '../lib/session.js';
import { messages } from '../i18n/messages.js';
import { useSession } from './session-context.js';
import { type Navigation, type Route, type RootName } from './navigation.js';

type Icon = (props: IconProps) => ReactNode;

interface Item {
  /** Menyudan tanlanganda ochiladigan ildiz ekran. */
  root: RootName;
  label: (t: ReturnType<typeof messages>) => string;
  icon: Icon;
  /** `null` — hammaga ko'rinadi. */
  permission: PermissionCode | null;
  /**
   * Shu punkt "faol" hisoblanadigan yo'nalishlar.
   *
   * Ildizdan tashqari ichki ekranlar ham kiritiladi: buyurtma
   * kartochkasida turganda "Buyurtmalar" yonib turishi kerak, aks
   * holda xodim qayerdaligini yo'qotadi.
   */
  active?: Route['name'][];
}

interface Group {
  label: (t: ReturnType<typeof messages>) => string;
  items: Item[];
}

/**
 * Yon menyu tuzilishi — `demo/avtopartshop-demo.html` dagi bilan bir xil
 * tartib va guruhlash.
 *
 * TEPADAGI TUGMALAR QATORI OLIB TASHLANDI. Sabab o'lchangan: 18 ta
 * ekran gorizontal qatorga sig'maydi va u ikkinchi qatorga o'ralib
 * ketardi. Yon menyuda esa guruh sarlavhalari ish turini ajratadi —
 * operator "Umumiy" dan chiqmaydi, kontent menejer "Katalog" da
 * ishlaydi.
 *
 * Demodan ikki farq bor va ikkalasi ham ataylab:
 *   - "Import" qo'shildi — demoda yo'q, bizda Excel importi bor
 *   - "Ma'lumotnomalar" qo'shildi — sozlama, avtomobil katalogi,
 *     sinonim va banner shu yerda; ular kamdan-kam ochiladi va
 *     alohida punkt bo'lib menyuni cho'zib yuborardi
 */
const GROUPS: Group[] = [
  {
    label: (t) => t.navGroupGeneral,
    items: [
      {
        root: 'dashboard',
        label: (t) => t.navDashboard,
        icon: IconChart,
        permission: PERMISSIONS.REPORT_VIEW,
      },
      {
        /* Kuryerning yagona ish ekrani: faqat o'ziga biriktirilgan yetkazmalar. */
        root: 'deliveries',
        label: (t) => t.navDeliveries,
        icon: IconTracking,
        permission: PERMISSIONS.DELIVERY_MY_SHIPMENTS,
      },
      {
        root: 'queue',
        label: (t) => t.navQueue,
        icon: IconBolt,
        // Server ham shuni talab qiladi (`order.view_all`): ruxsatsiz xodimga (kuryer, kontent)
        // ochilmaydigan, 403 beradigan punkt ko'rsatilmaydi.
        permission: PERMISSIONS.ORDER_VIEW_ALL,
        active: ['queue', 'bucket'],
      },
      {
        root: 'orders',
        label: (t) => t.navOrders,
        icon: IconOrders,
        permission: PERMISSIONS.ORDER_VIEW_ALL,
        active: ['orders', 'order'],
      },
      {
        /*
          To'lov navbati — faqat TASDIQLAY oladiganga. Operator uchun
          bu ekranda bajariladigan ish yo'q: u qatorni yozadi, lekin
          tasdiqlay olmaydi (vazifalar ajratilishi).
        */
        root: 'payments',
        label: (t) => t.navPayments,
        icon: IconCard,
        permission: PERMISSIONS.PAYMENT_CONFIRM,
      },
      {
        root: 'consignments',
        label: (t) => t.navConsignments,
        icon: IconTruck,
        permission: PERMISSIONS.CONSIGNMENT_MANAGE,
      },
    ],
  },
  {
    label: (t) => t.navGroupCatalog,
    items: [
      {
        root: 'products',
        label: (t) => t.navProducts,
        icon: IconWrench,
        permission: PERMISSIONS.CATALOG_VIEW,
        active: ['products', 'product'],
      },
      {
        root: 'categories',
        label: (t) => t.navCategories,
        icon: IconFolders,
        permission: PERMISSIONS.REFERENCE_MANAGE,
      },
      {
        root: 'import',
        label: (t) => t.navImport,
        icon: IconUpload,
        permission: PERMISSIONS.IMPORT_RUN,
      },
    ],
  },
  {
    label: (t) => t.navGroupAnalytics,
    items: [
      {
        root: 'searchLog',
        label: (t) => t.navSearchLog,
        icon: IconSearch,
        permission: PERMISSIONS.REPORT_VIEW,
      },
      {
        root: 'garageStats',
        label: (t) => t.navGarage,
        icon: IconCar,
        permission: PERMISSIONS.REPORT_VIEW,
      },
      {
        root: 'regionStats',
        label: (t) => t.navRegions,
        icon: IconMap,
        permission: PERMISSIONS.REPORT_VIEW,
      },
    ],
  },
  {
    label: (t) => t.navGroupShops,
    items: [
      {
        root: 'shops',
        label: (t) => t.refShops,
        icon: IconBuilding,
        permission: PERMISSIONS.REFERENCE_MANAGE,
      },
      {
        /*
          Yuk mashinasi EMAS: u yuqorida "Xitoy partiyalari" da
          ishlatilgan va bitta menyuda ikkita bir xil ikonka
          punktlarni ajratmay qo'yardi. Zona — joy, shuning uchun
          xarita belgisi.
        */
        root: 'zones',
        label: (t) => t.refZones,
        icon: IconPin,
        permission: PERMISSIONS.REFERENCE_MANAGE,
      },
    ],
  },
  {
    label: (t) => t.navGroupSystem,
    items: [
      {
        root: 'users',
        label: (t) => t.navUsers,
        icon: IconUsers,
        permission: PERMISSIONS.USER_MANAGE,
      },
      {
        root: 'statuses',
        label: (t) => t.refStatuses,
        icon: IconShuffle,
        permission: PERMISSIONS.REFERENCE_MANAGE,
      },
      {
        root: 'reference',
        label: (t) => t.navReference,
        icon: IconGear,
        permission: PERMISSIONS.REFERENCE_MANAGE,
      },
      {
        root: 'audit',
        label: (t) => t.navAudit,
        icon: IconScroll,
        permission: PERMISSIONS.AUDIT_VIEW,
      },
    ],
  },
];

export function Sidebar({ navigation }: { navigation: Navigation }): ReactNode {
  const { me, locale } = useSession();
  const t = messages(locale);
  const current = navigation.route.name;

  /*
   * Navbatdagi ish soni — demodagi `newCnt` nishoni.
   *
   * Menyuda turadi, chunki xodim boshqa ekranda ishlayotganda ham
   * yangi ish kelganini ko'rishi kerak. Ruxsati yetmagan guruhni
   * server javobga qo'shmaydi, ya'ni sanoq har rol uchun O'ZINIKI.
   */
  const queue = useQuery({
    // `WorkQueueScreen` bilan BIR XIL kalit — navbat ekraniga
    // o'tilganda ikkinchi so'rov ketmaydi, kesh ishlaydi.
    queryKey: ['work-queue'],
    queryFn: ({ signal }) => api.workQueueSummary(signal),
    refetchInterval: 60_000,
  });
  const pending = queue.data?.buckets.reduce((sum, bucket) => sum + bucket.count, 0);

  return (
    <nav className="adm-side" aria-label={t.navLabel}>
      {GROUPS.map((group) => {
        const visible = group.items.filter(
          (item) => item.permission === null || can(me, item.permission),
        );
        // Butun guruh yopiq bo'lsa sarlavhasi ham chiqmaydi — bo'sh
        // sarlavha "bu yerda nimadir bor edi" degan taassurot beradi.
        if (visible.length === 0) return null;

        return (
          <div className="adm-side__group" key={group.label(t)}>
            <p className="adm-side__group-title">{group.label(t)}</p>
            {visible.map((item) => {
              const Icon = item.icon;
              const active = (item.active ?? [item.root]).includes(current);
              return (
                <button
                  key={item.root}
                  type="button"
                  className="adm-side__item"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    navigation.switchRoot(item.root);
                  }}
                >
                  <Icon size={18} />
                  <span className="adm-side__label">{item.label(t)}</span>
                  {/* NOL nishon ko'rsatilmaydi — "0" ish borday tuyuladi. */}
                  {item.root === 'queue' && pending !== undefined && pending > 0 && (
                    <span className="adm-side__badge">{String(pending)}</span>
                  )}
                </button>
              );
            })}

            {/*
              "Tovar qo'shish" — demodagi `newProduct()`. Ildiz ekran
              EMAS: u forma va undan "orqaga" ro'yxatga qaytishi
              kerak, shuning uchun stackka qo'shiladi.
            */}
            {group.label(t) === t.navGroupCatalog && can(me, PERMISSIONS.PRODUCT_CREATE) && (
              <button
                type="button"
                className="adm-side__item"
                aria-current={current === 'product' ? 'page' : undefined}
                onClick={() => {
                  navigation.openProduct(null);
                }}
              >
                <IconPlus size={18} />
                <span className="adm-side__label">{t.navProductNew}</span>
              </button>
            )}
          </div>
        );
      })}
    </nav>
  );
}
